// ABOUTME: The proposal window pro-core allows (earliest, latest, length, how many) and the rows it refused with their
// ABOUTME: reasons, read defensively: a response without them leaves the propose form exactly as it was (#484, #1907).
import { formatInTimeZone } from 'date-fns-tz';
import { ApiError } from 'utils/api';
import { localInputToIso } from './format';
import { ProposalWindow } from './types';

export const TIME_OPTIONS_INVALID = 'error.interview.time-options-invalid';

export type InvalidProposalReason = 'too-soon' | 'too-late' | 'too-short';

/** One refused range of a proposal, as pro-core names it in `values.invalid`. */
export interface InvalidProposal {
  startsAt: string;
  reason: InvalidProposalReason;
}

const REASONS: InvalidProposalReason[] = ['too-soon', 'too-late', 'too-short'];

const isInvalidProposal = (item: unknown): item is InvalidProposal => {
  if (typeof item !== 'object' || item === null) return false;
  const { startsAt, reason } = item as Record<string, unknown>;
  return (
    typeof startsAt === 'string' &&
    REASONS.includes(reason as InvalidProposalReason)
  );
};

/**
 * The rows pro-core refused, each with its reason. Empty for any other error and for a refusal that does not name its
 * rows (an image older than #1907): the form then keeps the general message alone, as before.
 */
export const invalidProposalsOf = (error: unknown): InvalidProposal[] => {
  if (!(error instanceof ApiError) || error.code !== TIME_OPTIONS_INVALID) {
    return [];
  }
  // ErrorValues is typed as a string map, but pro-core's values are JSON: `invalid` arrives as an array of objects.
  const invalid = (error.values as unknown as Record<string, unknown>)?.invalid;
  return Array.isArray(invalid) ? invalid.filter(isInvalidProposal) : [];
};

/** The window the refusal itself names, so a reason can be told even when the card read carried no window. */
export const refusalWindowOf = (
  error: unknown
): Partial<ProposalWindow> | null => {
  if (!(error instanceof ApiError) || error.code !== TIME_OPTIONS_INVALID) {
    return null;
  }
  const values = error.values as unknown as Record<string, unknown>;
  const window: Partial<ProposalWindow> = {};
  if (typeof values?.earliest === 'string') window.earliest = values.earliest;
  if (typeof values?.latest === 'string') window.latest = values.latest;
  if (typeof values?.durationMinutes === 'number') {
    window.durationMinutes = values.durationMinutes;
  }
  return window;
};

/** An instant as the value of a `datetime-local` input, read as wall-clock time in `timeZone`. */
export const isoToLocalInput = (iso: string, timeZone: string): string =>
  formatInTimeZone(new Date(iso), timeZone, "yyyy-MM-dd'T'HH:mm");

/** A `datetime-local` value plus whole minutes, still as a wall-clock value in `timeZone`. */
export const addMinutesToLocalInput = (
  value: string,
  minutes: number,
  timeZone: string
): string => {
  const end = new Date(
    Date.parse(localInputToIso(value, timeZone)) + minutes * 60_000
  );
  return isoToLocalInput(end.toISOString(), timeZone);
};

/** Whether a form row is the range pro-core refused: the same instant, however each side spelled it. */
export const isSameInstant = (a: string, b: string): boolean =>
  Date.parse(a) === Date.parse(b);

/** Whole hours of notice from now to `earliest`, at least one, for "mindestens n Std. im Voraus". */
export const noticeHours = (earliest: string, now: Date): number =>
  Math.max(1, Math.round((Date.parse(earliest) - now.getTime()) / 3_600_000));
